"use client";

import { useState, useEffect } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const PurchaseBillMerchants = () => {
  const [merchants, setMerchants] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [newMerchant, setNewMerchant] = useState({
    merchant_name: "",
    merchant_emails: "",
    merchant_phone: "",
    merchant_address: "",
    merchant_state: "",
  });

  /* ================= FETCH MERCHANTS ================= */
  useEffect(() => {
    fetchMerchants();
  }, []);

  const fetchMerchants = async () => {
    try {
      setIsLoading(true);
      const res = await axios.get(
        "https://logicx.premierlam.com/api/get-pixel-purchase-bills-merchant",
        {
          headers: {
            authorization: localStorage.getItem("accessToken"),
          },
        }
      );
      setMerchants(res.data.data || []);
    } catch (err) {
      toast.error("Failed to fetch merchants");
    } finally {
      setIsLoading(false);
    }
  };

  /* ================= ADD MERCHANT ================= */
  const handleAddMerchant = async (e) => {
    e.preventDefault();

    // 🔴 Mobile validation
    if (newMerchant.merchant_phone.length !== 10) {
      toast.error("Mobile number must be exactly 10 digits");
      return;
    }

    try {
      setIsSubmitting(true);

      await axios.post(
        "https://logicx.premierlam.com/api/add-pixel-purchase-bills-merchant",
        newMerchant,
        {
          headers: {
            authorization: localStorage.getItem("accessToken"),
            "Content-Type": "application/json",
          },
        }
      );

      toast.success("Merchant added successfully!");
      setIsAddDialogOpen(false);
      setNewMerchant({
        merchant_name: "",
        merchant_emails: "",
        merchant_phone: "",
        merchant_address: "",
        merchant_state: "",
      });

      fetchMerchants();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to add merchant");
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredMerchants = merchants.filter((m) =>
    m.merchant_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const d = new Date(dateString);
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="min-h-screen bg-gray-50 p-2">
        <div className="max-w-7xl mx-auto">

          {/* HEADER */}
          <div className="flex justify-between items-start mb-6">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900 mb-1">
                Purchase Bill Merchants
              </h1>
              <p className="text-gray-500">Manage purchase bill merchants</p>
            </div>

            <button
              onClick={() => setIsAddDialogOpen(true)}
              className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"
            >
               <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg> Add Merchant
            </button>
          </div>

          {/* TABLE */}
          <div className="bg-white rounded-lg shadow-sm border p-6">

            {/* SEARCH */}
            <input
              type="text"
              placeholder="Search merchant"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="mb-4 px-4 py-2 border rounded-lg w-64"
            />

            <div className="overflow-x-auto">
              {isLoading ? (
                <div className="text-center py-6">Loading...</div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-gray-600">
                      <th className="py-3 px-4 text-left">Merchant Name</th>
                      <th className="py-3 px-4 text-left">Email</th>
                      <th className="py-3 px-4 text-left">Phone</th>
                      <th className="py-3 px-4 text-left">Address</th>
                      <th className="py-3 px-4 text-left">State</th>
                      <th className="py-3 px-4 text-left">Created At</th>
                      <th className="py-3 px-4 text-left">Added By</th>
                    </tr>
                  </thead>

                  <tbody>
                    {filteredMerchants.map((m) => (
                      <tr key={m.id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4">{m.merchant_name}</td>
                        <td className="py-3 px-4">{m.merchant_emails}</td>
                        <td className="py-3 px-4 whitespace-nowrap">
                          {m.merchant_phone}
                        </td>
                        <td className="py-3 px-4">{m.merchant_address}</td>
                        <td className="py-3 px-4">{m.merchant_state}</td>
                        <td className="py-3 px-4 whitespace-nowrap">
                          {formatDate(m.created_at)}
                        </td>
                        <td className="py-3 px-4">{m.added_by}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* ADD MERCHANT MODAL */}
          {isAddDialogOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
              <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md relative">
                <button
                  className="absolute top-2 right-2 text-gray-400"
                  onClick={() => setIsAddDialogOpen(false)}
                >
                  ✖
                </button>

                <h2 className="text-lg font-semibold mb-4">
                  Add Purchase Bill Merchant
                </h2>

                <form onSubmit={handleAddMerchant} className="space-y-3">
                  <input
                    type="text"
                    placeholder="Merchant Name"
                    value={newMerchant.merchant_name}
                    onChange={(e) =>
                      setNewMerchant({ ...newMerchant, merchant_name: e.target.value })
                    }
                    className="w-full border rounded px-3 py-2"
                    required
                  />

                  <input
                    type="email"
                    placeholder="Merchant Email"
                    value={newMerchant.merchant_emails}
                    onChange={(e) =>
                      setNewMerchant({ ...newMerchant, merchant_emails: e.target.value })
                    }
                    className="w-full border rounded px-3 py-2"
                    required
                  />

                  {/* MOBILE NUMBER VALIDATION */}
                  <input
                    type="text"
                    placeholder="Merchant Phone (10 digits)"
                    value={newMerchant.merchant_phone}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^\d{0,10}$/.test(value)) {
                        setNewMerchant({ ...newMerchant, merchant_phone: value });
                      }
                    }}
                    onBlur={() => {
                      if (newMerchant.merchant_phone.length !== 10) {
                        toast.error("Mobile number must be 10 digits");
                      }
                    }}
                    className="w-full border rounded px-3 py-2"
                    required
                  />

                  <input
                    type="text"
                    placeholder="Merchant Address"
                    value={newMerchant.merchant_address}
                    onChange={(e) =>
                      setNewMerchant({ ...newMerchant, merchant_address: e.target.value })
                    }
                    className="w-full border rounded px-3 py-2"
                    required
                  />

                  <input
                    type="text"
                    placeholder="Merchant State"
                    value={newMerchant.merchant_state}
                    onChange={(e) =>
                      setNewMerchant({ ...newMerchant, merchant_state: e.target.value })
                    }
                    className="w-full border rounded px-3 py-2"
                    required
                  />

                  <div className="flex justify-end gap-3 pt-3">
                    <button
                      type="button"
                      onClick={() => setIsAddDialogOpen(false)}
                      className="px-4 py-2 bg-gray-100 rounded"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="px-4 py-2 bg-orange-500 text-white rounded"
                    >
                      {isSubmitting ? "Adding..." : "Add Merchant"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

        </div>
      </div>
    </>
  );
};

export default PurchaseBillMerchants;
